import { format, isPast, isToday } from "date-fns";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Stack,
  Avatar,
  Tooltip,
} from "@mui/material";
import { CalendarToday, MoreHoriz } from "@mui/icons-material";
import { useState } from "react";
import { useKanban } from "./kanban-provider";


const PRIORITY_COLOR = {
  high:   "#ef4444",
  medium: "#f97316",
  low:    "#22c55e",
};

const PRIORITY_LABEL = {
  high:   "High",
  medium: "Medium",
  low:    "Low",
};


export default function TaskCard({ task, onEdit, isDragging = false, onDragStart, onDragEnd }) {
  const { deleteTask } = useKanban();
  const [anchorEl, setAnchorEl] = useState(null);

  const formatDate = (dateString) => {
    try { return format(new Date(dateString), "MMM d"); }
    catch { return ""; }
  };

  const isOverdue = task.dueDate && isPast(new Date(task.dueDate)) && !isToday(new Date(task.dueDate));
  const isDueToday = task.dueDate && isToday(new Date(task.dueDate));

  const dueDateColor = isOverdue
    ? "error.main"
    : isDueToday
      ? "warning.main"
      : "text.secondary";

  const assigneeInitials = task.assignee
    ? task.assignee.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : "?";

  return (
    <Card
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      sx={{
        cursor: isDragging ? "grabbing" : "grab",
        opacity: isDragging ? 0.5 : 1,
        boxShadow: isDragging ? 6 : 1,
        transition: "box-shadow 0.15s, opacity 0.15s",
        bgcolor: "background.paper",
        borderRadius: 1.5,
        overflow: "visible",
        "&:hover": { boxShadow: 4 },
        "&:hover .card-menu-btn": { opacity: 1 },
        position: "relative",
      }}
    >
      {/* Priority label bar */}
      <Box
        sx={{
          height: 5,
          borderRadius: "6px 6px 0 0",
          bgcolor: PRIORITY_COLOR[task.priority] || "grey.400",
        }}
      />

      <CardContent sx={{ p: 1.5, pb: "12px !important" }}>
        {/* Title row */}
        <Box 
          display="flex" 
          alignItems="flex-start" 
          justifyContent="space-between" 
          gap={0.5}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ flex: 1, lineHeight: 1.4, wordBreak: "break-word" }}
          >
            {task.title}
          </Typography>

          <IconButton
            className="card-menu-btn"
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              opacity: 0,
              transition: "opacity 0.15s",
              p: 0.25,
              flexShrink: 0,
              mt: -0.25,
            }}
          >
            <MoreHoriz sx={{ fontSize: 16 }} />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { minWidth: 140 } } }}
          >
            <MenuItem onClick={() => { onEdit(); setAnchorEl(null); }}>
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => { deleteTask(task._id); setAnchorEl(null); }}
              sx={{ color: "error.main" }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Box>

        {/* Description */}
        {task.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mt: 0.5,
              lineHeight: 1.4,
            }}
          >
            {task.description}
          </Typography>
        )}

        {/* Footer */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 1.25 }}
        >
          {/* Priority pill + due date */}
          <Stack direction="row" alignItems="center" gap={0.75}>
            <Box
              sx={{
                px: 0.75,
                py: 0.1,
                borderRadius: 0.75,
                bgcolor: PRIORITY_COLOR[task.priority] + "22",
                border: `1px solid ${PRIORITY_COLOR[task.priority]}44`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: PRIORITY_COLOR[task.priority],
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  lineHeight: 1.6,
                }}
              >
                {PRIORITY_LABEL[task.priority]}
              </Typography>
            </Box>

            {task.dueDate && (
              <Stack direction="row" alignItems="center" gap={0.25}>
                <CalendarToday sx={{ fontSize: 11, color: dueDateColor }} />
                <Typography 
                  variant="caption" 
                  sx={{ color: dueDateColor, fontSize: "0.7rem" }}
                >
                  {formatDate(task.dueDate)}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Assignee avatar */}
          {task.assignee && task.assignee !== "Unassigned" && (
            <Tooltip title={task.assignee}>
              <Avatar
                sx={{
                  width: 22,
                  height: 22,
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  bgcolor: "primary.main",
                }}
              >
                {assigneeInitials}
              </Avatar>
            </Tooltip>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
